export interface PointsPurchasePackSelection {
  packPts: number
  packCost: number
  bonus: number
  credited: number
  bonusPct: number
}

function getEligibleOffers(
  offers: ReadonlyArray<App.LoyaltyPurchasePointsOffer>,
): Array<App.LoyaltyPurchasePointsOffer> {
  return offers
    .filter(offer => offer.isEligibleNow !== false)
    .slice()
    .sort((a, b) => a.basePoints - b.basePoints)
}

function getPricedOffers(
  offers: ReadonlyArray<App.LoyaltyPurchasePointsOffer>,
): Array<App.LoyaltyPurchasePointsOffer> {
  return offers
    .filter(offer => offer.basePoints > 0)
    .slice()
    .sort((a, b) => a.basePoints - b.basePoints)
}

export function getActiveBonusPct(
  offers: ReadonlyArray<App.LoyaltyPurchasePointsOffer>,
): number {
  const withPercent = offers.find(offer => offer.bonusPointsPercent > 0)
  if (withPercent) {
    return withPercent.bonusPointsPercent / 100
  }

  const withBonus = offers.find(offer => offer.bonusPoints > 0 && offer.basePoints > 0)
  if (withBonus) {
    return withBonus.bonusPoints / withBonus.basePoints
  }

  return 0
}

/**
 * Picks the cheapest points-purchase pack(s) to cover a reward shortfall, accounting
 * for bonus points from an active BONUS_POINTS campaign.
 */
export function pickPointsPurchasePacksForShortfall(
  offers: ReadonlyArray<App.LoyaltyPurchasePointsOffer>,
  shortfallPP: number,
): PointsPurchasePackSelection | undefined {
  if (shortfallPP <= 0 || offers.length === 0) {
    return undefined
  }

  const eligibleOffers = getEligibleOffers(offers)
  const pricedOffers = eligibleOffers.length > 0 ? eligibleOffers : getPricedOffers(offers)
  if (pricedOffers.length === 0) {
    return undefined
  }

  const bonusPct = getActiveBonusPct(pricedOffers)
  const rawNeeded = Math.ceil(shortfallPP / (1 + bonusPct))

  const singlePack = pricedOffers
    .filter(offer => offer.basePoints >= rawNeeded)
    .sort((a, b) => a.publicPrice - b.publicPrice)[0]

  const selectedOffers = singlePack
    ? [singlePack]
    : (() => {
        const largest = pricedOffers[pricedOffers.length - 1]
        if (!largest || largest.basePoints <= 0) {
          return []
        }

        const packs: Array<App.LoyaltyPurchasePointsOffer> = []
        let remainingBase = rawNeeded

        while (remainingBase > 0) {
          const covering = pricedOffers.find(offer => offer.basePoints >= remainingBase)
          if (covering) {
            packs.push(covering)
            break
          }
          packs.push(largest)
          remainingBase -= largest.basePoints
        }

        return packs
      })()

  if (selectedOffers.length === 0) {
    return undefined
  }

  const packPts = selectedOffers.reduce((sum, offer) => sum + offer.basePoints, 0)
  const packCost = selectedOffers.reduce((sum, offer) => sum + offer.publicPrice, 0)
  const bonus = Math.round(packPts * bonusPct)
  const credited = packPts + bonus

  return {
    packPts,
    packCost,
    bonus,
    credited,
    bonusPct,
  }
}
